// src/Hooks/useMapInitialView.js

import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../Firebase/firebase';

const FALLBACK_CENTER = { lat: -33.8991007168729, lng: 151.16924565077124 };
const FALLBACK_ZOOM = 9.123456;

export const useMapInitialView = () => {
  const [viewConfig, setViewConfig] = useState(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      let targetCenter = FALLBACK_CENTER;
      let targetZoom = FALLBACK_ZOOM;
      let targetId = 'fallback';
      let targetName = 'Default View';
      let specificViewFound = false;

      try {
        // 1. Try to get a user-specific view if they are logged in
        if (user && user.email) {
          const emailKey = user.email.toLowerCase().trim();
          const permDocRef = doc(db, 'permittedUsers', emailKey);
          const permSnap = await getDoc(permDocRef);

          if (permSnap.exists()) {
            const userData = permSnap.data();
            if (userData.initialViewId) {
              const targetIdTemp = String(userData.initialViewId).trim();
              const viewDocRef = doc(db, 'initialViews', targetIdTemp);
              const viewSnap = await getDoc(viewDocRef);

              if (viewSnap.exists()) {
                const vData = viewSnap.data();
                if (vData.lat && vData.lng && vData.zoom) {
                  targetCenter = {
                    lat: parseFloat(vData.lat),
                    lng: parseFloat(vData.lng),
                  };
                  targetZoom = parseFloat(vData.zoom);
                  targetId = viewSnap.id;
                  targetName = vData.name || 'Unnamed View';
                  specificViewFound = true;
                }
              }
            }
          }
        }

        // 2. AMENDED: If no specific view was found (either because they are logged out OR don't have a personal view set), fetch the Global Default
        if (!specificViewFound) {
          const qDefault = query(
            collection(db, 'initialViews'),
            where('isDefault', '==', true),
          );
          const querySnapshot = await getDocs(qDefault);

          if (!querySnapshot.empty) {
            const defaultDoc = querySnapshot.docs[0];
            const vData = defaultDoc.data();
            if (vData.lat && vData.lng && vData.zoom) {
              targetCenter = { lat: parseFloat(vData.lat), lng: parseFloat(vData.lng) };
              targetZoom = parseFloat(vData.zoom);
              targetId = defaultDoc.id;
              targetName = vData.name || 'Default View';
            }
          }
        }
      } catch (error) {
        if (error.code !== 'permission-denied') {
          console.error('Error determining initial view:', error);
        }
      }

      setViewConfig({
        id: targetId,
        name: targetName,
        center: targetCenter,
        zoom: targetZoom,
      });
    });

    return () => unsubscribeAuth();
  }, []);

  return { viewConfig };
};
